import { Invoice, InvoiceAction } from 'types';

type InvoiceActionEnabledMapType = {
  viewEnabled: boolean;
  editEnabled: boolean;
  collectEnabled: boolean;
  refundEnabled: boolean;
  voidEnabled: boolean;
  reopenEnabled: boolean;
  sendEnabled: boolean;
};

/**
 * The invoice fields these rules read.
 *
 * Takes the invoice rather than the bare status because `editEnabled` now has two sources: the
 * unpaid statuses that open the full edit form, and the server's `isPaymentDateEditable`, which
 * opens the Payment Date picker alone. The two call sites have to agree on it, and a shared
 * argument is the cheapest way to keep them honest.
 */
type InvoiceActionSubject = Pick<Invoice, 'status' | 'isPaymentDateEditable' | 'total'>;

export const getInvoiceActionEnabledMap = ({
  status: invoiceStatus,
  isPaymentDateEditable,
  total,
}: InvoiceActionSubject): InvoiceActionEnabledMapType => {
  return {
    viewEnabled: true,
    editEnabled:
      ['UPCOMING', 'PAST DUE', 'PAYMENT FAILED', 'FAILED PAST DUE'].includes(invoiceStatus) ||
      isPaymentDateEditable,
    collectEnabled: ['UPCOMING', 'PAST DUE', 'PAYMENT FAILED', 'FAILED PAST DUE'].includes(invoiceStatus),
    // A $0 invoice settled with no payment, so there is nothing to refund.
    refundEnabled: ['PAID', 'PARTIALLY REFUNDED'].includes(invoiceStatus) && total > 0,
    voidEnabled: ['UPCOMING', 'PAST DUE', 'PAYMENT FAILED', 'FAILED PAST DUE'].includes(invoiceStatus),
    reopenEnabled: ['VOID', 'REFUNDED'].includes(invoiceStatus),
    sendEnabled: ['PAID', 'PARTIALLY REFUNDED'].includes(invoiceStatus),
  };
};

export const INVOICE_ACTION_TO_READABLE_TITLE_MAP: Record<InvoiceAction['action'] | string, string> = {
  CREATED: 'Created',
  EDITED: 'Edited',
  NOTES_EDITED: 'Note Added/Edited',
  PAYMENT_DATE_EDITED: 'Payment Date Edited',
  PAYMENT_INITIATED: 'Payment Pending',
  PAYMENT_SUCCEEDED: 'Paid',
  PAYMENT_FAILED: 'Payment Failed',
  BECAME_PAST_DUE: 'Past Due',
  VOIDED: 'Voided',
  REOPENDED: 'Reopened',
  REFUND_INITIATED: 'Refund Pending',
  REFUND_SUCCEEDED: 'Refund Succeeded',
  REFUND_FAILED: 'Refund Failed',
  PACKAGE_DELETED: 'Package has been deleted',
};
